import { api } from '../services/api';
import { useApi } from '../hooks/useApi';
import RankingsTable from '../components/Rankings/RankingsTable';
import Loading from '../components/common/Loading';
import ErrorMessage from '../components/common/ErrorMessage';
import './RankingsPage.css';

function formatDate(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function RankingsPage() {
  const { data: rankings, loading, error, refetch } = useApi(api.getRankings);
  const { data: invitational } = useApi(api.getInvitationalSettings);

  const dateFrom = formatDate(invitational?.dateFrom);
  const dateTo = formatDate(invitational?.dateTo);
  const hasDateRange = dateFrom || dateTo;

  return (
    <div className="rankings-page">
      <section className="rankings-page__header section section--dark">
        <div className="container">
          <h1 className="rankings-page__title staatliches-regular">
            Classement des Joueurs
          </h1>
          <p className="rankings-page__subtitle chiron-goround-tc-body">
            Classement basé sur les performances en tournois Champions Collective. Les points obtenables varient selon le placement et le nombre de participants.
          </p>
          {hasDateRange && (
            <p className="rankings-page__date-range chiron-goround-tc-body">
              Période : {dateFrom ? `Du ${dateFrom}` : 'Début'} {dateTo ? `au ${dateTo}` : ''}
            </p>
          )}
        </div>
      </section>

      {/* Qualification info */}
      {invitational && (
        <section className="rankings-page__qualification section">
          <div className="container">
            <h2 className="rankings-page__qualification-title staatliches-regular">
              Qualification pour l'Invitational
            </h2>
            <div className="rankings-page__qualification-info card">
              <p className="chiron-goround-tc-body">
                Les joueurs ayant le statut <strong>« Qualifié »</strong> dans le classement seront invités au tournoi Invitational
                {hasDateRange ? ` de cette période` : ''}.
              </p>
              <ul className="rankings-page__qualification-rules chiron-goround-tc-body">
                {invitational.autoQualifyWinners && (
                  <li>Les vainqueurs de tournoi sont automatiquement qualifiés.</li>
                )}
                {invitational.qualificationType === 'points' && (
                  <li>Les joueurs ayant au moins <strong>{invitational.threshold} points</strong> sont qualifiés.</li>
                )}
                {invitational.qualificationType === 'rank' && (
                  <li>Les <strong>{invitational.threshold} premiers</strong> joueurs du classement sont qualifiés.</li>
                )}
                {invitational.qualificationType === 'rank' && (
                  <li>Les joueurs ayant le même nombre de points que le dernier joueur qualifié seront également qualifiés.</li>
                )}
              </ul>
              <p className="rankings-page__qualification-note chiron-goround-tc-body">
                Notre circuit est 100% gratuit et ne proposera pas de cashprize.
              </p>
              <p className="rankings-page__qualification-note2 chiron-goround-tc-body">
                Il s'agit d'un mini-circuit compétitif online fait pour progresser dans un environnement accessible pour tous !
              </p>
            </div>
          </div>
        </section>
      )}

      <section className="rankings-page__content section">
        <div className="container">
          {loading && <Loading text="Chargement du classement..." />}
          {error && <ErrorMessage message={error} onRetry={refetch} />}
          {!loading && !error && (
            <RankingsTable rankings={rankings} invitational={invitational} />
          )}
        </div>
      </section>

      <section className="rankings-page__legend section section--alt">
        <div className="container">
          <h2 className="rankings-page__legend-title staatliches-regular">Système de Points</h2>
          <p className="rankings-page__legend-note chiron-goround-tc-body">
            Les points sont attribués uniquement si le tournoi atteint le nombre minimum de participants requis pour chaque palier.
          </p>
          <div className="rankings-page__legend-grid">
            <div className="rankings-page__legend-item card">
              <h3 className="staatliches-regular">1er</h3>
              <p className="chiron-goround-tc-body">100 pts</p>
              <span className="rankings-page__legend-min chiron-goround-tc-light">min. 9 joueurs</span>
            </div>
            <div className="rankings-page__legend-item card">
              <h3 className="staatliches-regular">2e</h3>
              <p className="chiron-goround-tc-body">85 pts</p>
              <span className="rankings-page__legend-min chiron-goround-tc-light">min. 9 joueurs</span>
            </div>
            <div className="rankings-page__legend-item card">
              <h3 className="staatliches-regular">3e - 4e</h3>
              <p className="chiron-goround-tc-body">70 pts</p>
              <span className="rankings-page__legend-min chiron-goround-tc-light">min. 17 joueurs</span>
            </div>
            <div className="rankings-page__legend-item card">
              <h3 className="staatliches-regular">5e - 8e</h3>
              <p className="chiron-goround-tc-body">55 pts</p>
              <span className="rankings-page__legend-min chiron-goround-tc-light">min. 17 joueurs</span>
            </div>
            <div className="rankings-page__legend-item card">
              <h3 className="staatliches-regular">9e - 16e</h3>
              <p className="chiron-goround-tc-body">40 pts</p>
              <span className="rankings-page__legend-min chiron-goround-tc-light">min. 33 joueurs</span>
            </div>
            <div className="rankings-page__legend-item card">
              <h3 className="staatliches-regular">17e - 32e</h3>
              <p className="chiron-goround-tc-body">20 pts</p>
              <span className="rankings-page__legend-min chiron-goround-tc-light">min. 65 joueurs</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
